import React, { useEffect, useMemo, useState } from 'react';
import firebase from 'firebase/compat/app';
import { useHistory, useLocation } from 'react-router-dom';
import {
    backfillMetaLeadSource,
    configureMetaLeadSource,
    createMetaLeadSource,
    configureGoogleSheetsLeadSource,
    createGoogleSheetsLeadSourceSpreadsheet,
    deleteLeadSourceIntegration,
    createLeadSourceIntegration,
    getMetaLeadSourceFormSample,
    getLeadSourceIntegrations,
    getTeam,
    listGoogleSheetsLeadSourceSpreadsheets,
    listMetaLeadSourceForms,
    listMetaLeadSourcePages,
    pauseLeadSourceIntegration,
    replayMetaLeadSourceFailedEvent,
    resumeLeadSourceIntegration,
    runGoogleSheetsLeadSourceSync,
    setupGoogleSheetsLeadSourcePush,
    startMetaLeadSource,
    startGoogleSheetsLeadSource,
    testMetaLeadSource,
    testGoogleSheetsLeadSourcePush,
    testLeadSourceIntegration,
    listMetaLeadSourceFailedEvents,
} from '../utils/crmApi';
import { useToast } from '../components/Toast';
import { isCrmPortalMode } from '../utils/crmEnv';

const SOURCE_TYPES = [
    { value: 'webhook', label: 'Universal Webhook' },
    { value: 'google_sheets', label: 'Google Sheets' },
    { value: 'meta_lead_ads', label: 'Meta Lead Ads' },
];

const TARGET_FIELDS = [
    { key: 'fullName', label: 'Full Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'city', label: 'City' },
    { key: 'projectInterest', label: 'Project / Interest' },
    { key: 'notes', label: 'Notes' },
];

const defaultCreateState = {
    type: 'webhook',
    routingMode: 'fixed_owner',
    assigneeIds: [],
    metaConnected: false,
    metaPageId: '',
    metaFormIds: [],
    metaForms: [],
    metaPages: [],
    metaSourceFields: [],
    metaMapping: {},
    metaTransforms: {
        phoneNormalize: true,
        emailLower: true,
        utmExtract: true,
    },
    metaSampleLead: null,
};

const defaultGoogleConfig = {
    spreadsheetId: '',
    sheetName: '',
    headerRowIndex: 1,
    pollIntervalMinutes: 5,
    mode: 'polling',
};

const defaultTransforms = {
    phoneNormalize: true,
    emailLower: true,
    utmExtract: true,
};

const formatDateTime = (value) => {
    if (!value) return '-';
    try {
        return new Date(value).toLocaleString();
    } catch {
        return '-';
    }
};

const integrationTypeLabel = (type) => {
    if (type === 'google_sheets') return 'Google Sheets Source';
    if (type === 'meta_lead_ads' || type === 'facebook_lead_ads') return 'Meta Lead Ads Source';
    if (type === 'google_ads_attribution') return 'Google Ads Attribution Source';
    return 'Webhook Source';
};

const integrationTypeHint = (integration) => {
    if (integration?.type === 'google_sheets') {
        const mode = integration?.googleSheets?.mode || 'polling';
        const sheetName = integration?.googleSheets?.sheetName || '-';
        return `Mode: ${mode} | Sheet: ${sheetName}`;
    }
    if (integration?.type === 'meta_lead_ads') {
        const pageName = integration?.meta?.pageName || integration?.meta?.pageId || 'Meta Page';
        const formCount = Array.isArray(integration?.meta?.formIds) ? integration.meta.formIds.length : 0;
        const subscribed = integration?.meta?.subscribed ? 'Subscribed' : 'Not subscribed';
        return `${pageName} | ${formCount} form${formCount === 1 ? '' : 's'} | ${subscribed}`;
    }
    return `ID: ${integration?.id || '-'}`;
};

const LeadSourcesPage = () => {
    const history = useHistory();
    const location = useLocation();
    const { showSuccess, showError, showInfo } = useToast();
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [teamSeats, setTeamSeats] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [createState, setCreateState] = useState(defaultCreateState);
    const [creating, setCreating] = useState(false);
    const [createdSecret, setCreatedSecret] = useState(null);
    const [pendingById, setPendingById] = useState({});
    const [metaPagesLoading, setMetaPagesLoading] = useState(false);
    const [metaFormsLoading, setMetaFormsLoading] = useState(false);
    const [metaSampleLoading, setMetaSampleLoading] = useState(false);
    const [editingMetaIntegrationId, setEditingMetaIntegrationId] = useState('');
    const [metaFailedEventsByIntegration, setMetaFailedEventsByIntegration] = useState({});
    const [metaFailedEventsOpenFor, setMetaFailedEventsOpenFor] = useState('');
    const [metaFailedEventsLoading, setMetaFailedEventsLoading] = useState(false);

    const [googleSetupIntegrationId, setGoogleSetupIntegrationId] = useState('');
    const [googleConfig, setGoogleConfig] = useState(defaultGoogleConfig);
    const [googlePreview, setGooglePreview] = useState(null);
    const [googleMapping, setGoogleMapping] = useState({});
    const [googleTransforms, setGoogleTransforms] = useState(defaultTransforms);
    const [googleConfigSaving, setGoogleConfigSaving] = useState(false);
    const [googlePushData, setGooglePushData] = useState(null);
    const [googlePushLoading, setGooglePushLoading] = useState(false);
    const [googlePushTesting, setGooglePushTesting] = useState(false);
    const [googleSpreadsheets, setGoogleSpreadsheets] = useState([]);
    const [googleSheetNames, setGoogleSheetNames] = useState([]);
    const [googleCatalogLoading, setGoogleCatalogLoading] = useState(false);
    const [googleCreateSheetLoading, setGoogleCreateSheetLoading] = useState(false);

    const activeSeats = useMemo(() => (
        teamSeats
            .filter((seat) => String(seat?.status || 'active') === 'active')
            .map((seat) => ({
                seatId: String(seat.id || ''),
                userId: `seat:${seat.id}`,
                name: String(seat.name || seat.alias || seat.id),
                role: String(seat.role || '').toLowerCase(),
            }))
            .filter((seat) => seat.seatId)
    ), [teamSeats]);

    const selectedGoogleIntegration = useMemo(
        () => items.find((integration) => integration.id === googleSetupIntegrationId && integration.type === 'google_sheets') || null,
        [items, googleSetupIntegrationId],
    );

    const selectedSpreadsheetMeta = googleSpreadsheets.find((entry) => entry.id === googleConfig.spreadsheetId) || null;
    const isMetaWizard = createState.type === 'meta_lead_ads';
    const isMetaConfigureMode = isMetaWizard && !!editingMetaIntegrationId;
    const selectedMetaFormForSample = Array.isArray(createState.metaFormIds) && createState.metaFormIds.length > 0
        ? createState.metaFormIds[0]
        : '';

    const updatePending = (integrationId, value) => {
        setPendingById((prev) => ({ ...prev, [integrationId]: value }));
    };

    const resetCreateModal = () => {
        setCreateOpen(false);
        setEditingMetaIntegrationId('');
        setCreateState(defaultCreateState);
        setMetaPagesLoading(false);
        setMetaFormsLoading(false);
        setMetaSampleLoading(false);
    };

    const loadMetaPages = async () => {
        setMetaPagesLoading(true);
        try {
            const response = await listMetaLeadSourcePages();
            const pages = Array.isArray(response?.items) ? response.items : [];
            setCreateState((prev) => {
                const nextPageId = prev.metaPageId && pages.some((page) => page.pageId === prev.metaPageId)
                    ? prev.metaPageId
                    : (pages[0]?.pageId || '');
                return {
                    ...prev,
                    metaConnected: true,
                    metaPages: pages,
                    metaPageId: nextPageId,
                };
            });
            return pages;
        } catch (error) {
            showError(error.message || 'Failed to load Meta pages');
            return [];
        } finally {
            setMetaPagesLoading(false);
        }
    };

    const loadMetaForms = async (pageId) => {
        const normalizedPageId = String(pageId || '').trim();
        if (!normalizedPageId) {
            setCreateState((prev) => ({ ...prev, metaForms: [], metaFormIds: [], metaSourceFields: [], metaSampleLead: null }));
            return [];
        }
        setMetaFormsLoading(true);
        try {
            const response = await listMetaLeadSourceForms(normalizedPageId);
            const forms = Array.isArray(response?.items) ? response.items : [];
            setCreateState((prev) => ({
                ...prev,
                metaForms: forms,
                metaFormIds: (prev.metaFormIds || []).filter((id) => forms.some((form) => form.formId === id)),
            }));
            return forms;
        } catch (error) {
            showError(error.message || 'Failed to load Meta lead forms');
            return [];
        } finally {
            setMetaFormsLoading(false);
        }
    };

    const loadMetaSample = async (formId, pageId) => {
        const normalizedFormId = String(formId || '').trim();
        const normalizedPageId = String(pageId || '').trim();
        if (!normalizedFormId || !normalizedPageId) {
            showError('Select a Page and Form before loading sample fields');
            return null;
        }
        setMetaSampleLoading(true);
        try {
            const response = await getMetaLeadSourceFormSample(normalizedFormId, { pageId: normalizedPageId });
            const sourceFields = Array.isArray(response?.sourceFields) ? response.sourceFields : [];
            const autoMapping = response?.autoMapping && typeof response.autoMapping === 'object' ? response.autoMapping : {};
            setCreateState((prev) => ({
                ...prev,
                metaSourceFields: sourceFields,
                metaMapping: {
                    ...autoMapping,
                    ...(prev.metaMapping || {}),
                },
                metaSampleLead: response?.sampleLead || null,
            }));
            if (!response?.hasSample) {
                showInfo('No sample lead found yet for this form. You can still activate and backfill later.');
            }
            return response;
        } catch (error) {
            showError(error.message || 'Failed to load Meta sample fields');
            return null;
        } finally {
            setMetaSampleLoading(false);
        }
    };

    const handleMetaConnect = async (integrationId = '') => {
        try {
            const response = await startMetaLeadSource({
                integrationId: integrationId || undefined,
                frontendOrigin: window.location.origin,
            });
            const oauthUrl = String(response?.url || '').trim();
            if (!oauthUrl) throw new Error('Meta OAuth URL was not returned by server.');
            showInfo('Redirecting to Meta consent...');
            window.location.assign(oauthUrl);
        } catch (error) {
            showError(error.message || 'Failed to start Meta connection');
        }
    };

    const openMetaConfigureFlow = async (integration) => {
        const meta = integration?.meta || {};
        setEditingMetaIntegrationId(String(integration?.id || ''));
        setCreateOpen(true);
        setCreateState((prev) => ({
            ...defaultCreateState,
            type: 'meta_lead_ads',
            routingMode: prev?.routingMode || 'fixed_owner',
            assigneeIds: Array.isArray(prev?.assigneeIds) ? prev.assigneeIds : [],
            metaConnected: true,
            metaPageId: String(meta.pageId || ''),
            metaFormIds: Array.isArray(meta.formIds) ? meta.formIds : [],
            metaPages: [],
            metaForms: [],
            metaSourceFields: [],
            metaMapping: {},
            metaTransforms: defaultTransforms,
            metaSampleLead: null,
        }));
        const pages = await loadMetaPages();
        const pageId = String(meta.pageId || pages?.[0]?.pageId || '').trim();
        if (pageId) {
            await loadMetaForms(pageId);
        }
    };

    const loadMetaFailedEvents = async (integrationId) => {
        if (!integrationId) return;
        setMetaFailedEventsLoading(true);
        try {
            const response = await listMetaLeadSourceFailedEvents(integrationId, { limit: 20 });
            setMetaFailedEventsByIntegration((prev) => ({
                ...prev,
                [integrationId]: Array.isArray(response?.items) ? response.items : [],
            }));
            setMetaFailedEventsOpenFor(integrationId);
        } catch (error) {
            showError(error.message || 'Failed to load failed Meta events');
        } finally {
            setMetaFailedEventsLoading(false);
        }
    };

    const loadGoogleCatalog = async (integrationId, spreadsheetId) => {
        if (!integrationId) return;
        setGoogleCatalogLoading(true);
        try {
            const response = await listGoogleSheetsLeadSourceSpreadsheets(integrationId, {
                spreadsheetId: String(spreadsheetId || '').trim() || undefined,
            });
            const spreadsheets = Array.isArray(response?.spreadsheets) ? response.spreadsheets : [];
            const sheetNames = Array.isArray(response?.sheetNames) ? response.sheetNames : [];
            setGoogleSpreadsheets(spreadsheets);
            setGoogleSheetNames(sheetNames);
        } catch (error) {
            showError(error.message || 'Failed to load Google Sheets list');
        } finally {
            setGoogleCatalogLoading(false);
        }
    };

    const loadIntegrations = async () => {
        setLoading(true);
        try {
            const response = await getLeadSourceIntegrations();
            setItems(Array.isArray(response?.items) ? response.items : []);
        } catch (error) {
            showError(error.message || 'Failed to load integrations');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadIntegrations();
    }, []);

    useEffect(() => {
        const loadTeam = async () => {
            try {
                const team = await getTeam();
                setTeamSeats(Array.isArray(team?.seats) ? team.seats : []);
            } catch {
                setTeamSeats([]);
            }
        };
        loadTeam();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const connected = params.get('gs_connected');
        if (!connected) return;
        if (connected === '1') {
            const integrationId = String(params.get('gs_integration_id') || '').trim();
            if (integrationId) {
                setGoogleSetupIntegrationId(integrationId);
                showSuccess('Google Sheets connected. Complete mapping to enable sync.');
            }
        } else {
            const message = String(params.get('gs_error') || 'Google Sheets connection failed');
            const requestId = String(params.get('requestId') || '');
            showError(requestId ? `${message} (RequestId: ${requestId})` : message);
        }
        const clean = new URLSearchParams(location.search);
        ['gs_connected', 'gs_integration_id', 'gs_error', 'requestId'].forEach((key) => clean.delete(key));
        history.replace({
            pathname: location.pathname,
            search: clean.toString() ? `?${clean.toString()}` : '',
        });
        loadIntegrations();
    }, [history, location.pathname, location.search]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const metaConnected = params.get('meta_connected');
        const metaReconnected = params.get('meta_reconnected');
        if (!metaConnected && !metaReconnected) return;

        const run = async () => {
            const requestId = String(params.get('requestId') || '');
            const errorMessage = String(params.get('meta_error') || '').trim();
            const reconnectedIntegrationId = String(params.get('meta_integration_id') || '').trim();

            if (metaReconnected) {
                if (metaReconnected === '1') {
                    showSuccess('Meta reconnected successfully');
                    await loadIntegrations();
                    if (reconnectedIntegrationId) {
                        const current = items.find((entry) => entry.id === reconnectedIntegrationId);
                        if (current) {
                            await openMetaConfigureFlow(current);
                        }
                    }
                } else {
                    showError(requestId ? `${errorMessage || 'Meta reconnect failed'} (RequestId: ${requestId})` : (errorMessage || 'Meta reconnect failed'));
                }
            } else if (metaConnected) {
                if (metaConnected === '1') {
                    showSuccess('Meta connected. Select a Page and form(s) to activate.');
                    setEditingMetaIntegrationId('');
                    setCreateOpen(true);
                    setCreateState({
                        ...defaultCreateState,
                        type: 'meta_lead_ads',
                        metaConnected: true,
                    });
                    const pages = await loadMetaPages();
                    const firstPageId = String(pages?.[0]?.pageId || '').trim();
                    if (firstPageId) {
                        await loadMetaForms(firstPageId);
                    }
                } else {
                    showError(requestId ? `${errorMessage || 'Meta connection failed'} (RequestId: ${requestId})` : (errorMessage || 'Meta connection failed'));
                }
                await loadIntegrations();
            }
        };

        run().finally(() => {
            const clean = new URLSearchParams(location.search);
            ['meta_connected', 'meta_reconnected', 'meta_error', 'meta_integration_id', 'requestId'].forEach((key) => clean.delete(key));
            history.replace({
                pathname: location.pathname,
                search: clean.toString() ? `?${clean.toString()}` : '',
            });
        });
    }, [history, location.pathname, location.search]);

    useEffect(() => {
        if (!selectedGoogleIntegration) return;
        const gs = selectedGoogleIntegration.googleSheets || {};
        setGoogleConfig({
            spreadsheetId: String(gs.spreadsheetId || ''),
            sheetName: String(gs.sheetName || ''),
            headerRowIndex: Math.max(1, Number(gs.headerRowIndex || 1)),
            pollIntervalMinutes: Math.max(1, Number(gs.pollIntervalMinutes || 5)),
            mode: gs.mode === 'push' ? 'push' : 'polling',
        });
        setGooglePreview(null);
        setGooglePushData(null);
        loadGoogleCatalog(selectedGoogleIntegration.id, gs.spreadsheetId);
    }, [selectedGoogleIntegration?.id]);

    const handleAssigneeToggle = (userId) => {
        setCreateState((prev) => {
            if (prev.routingMode === 'fixed_owner') {
                return { ...prev, assigneeIds: [userId] };
            }
            const exists = prev.assigneeIds.includes(userId);
            return {
                ...prev,
                assigneeIds: exists ? prev.assigneeIds.filter((entry) => entry !== userId) : [...prev.assigneeIds, userId],
            };
        });
    };

    const handleRoutingModeChange = (mode) => {
        setCreateState((prev) => {
            if (mode === 'fixed_owner' && prev.assigneeIds.length > 1) {
                return { ...prev, routingMode: mode, assigneeIds: [prev.assigneeIds[0]] };
            }
            return { ...prev, routingMode: mode };
        });
    };

    const handleCopy = async (label, value) => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            showInfo(`${label} copied`);
        } catch {
            showError(`Unable to copy ${label.toLowerCase()}`);
        }
    };

    const handleCreate = async () => {
        if (creating) return;
        setCreating(true);
        try {
            const franchiseId = String(firebase.auth().currentUser?.uid || '').trim();
            if (!franchiseId) {
                throw new Error('Unable to resolve franchise context from current auth session.');
            }
            const isMetaConfigure = createState.type === 'meta_lead_ads' && !!editingMetaIntegrationId;
            const assigneeIds = activeSeats
                .filter((seat) => createState.assigneeIds.includes(seat.userId))
                .map((seat) => seat.userId);
            if (!isMetaConfigure) {
                if (createState.routingMode === 'round_robin' && assigneeIds.length < 2) {
                    throw new Error('Round robin needs at least 2 assignees.');
                }
                if (createState.routingMode === 'fixed_owner' && assigneeIds.length !== 1) {
                    throw new Error('Fixed owner needs exactly 1 assignee.');
                }
                if (assigneeIds.length === 0) {
                    throw new Error('Select at least one assignee.');
                }
            }

            if (createState.type === 'google_sheets') {
                const response = await startGoogleSheetsLeadSource({
                    franchiseId,
                    type: 'google_sheets',
                    routing: {
                        mode: createState.routingMode,
                        assignees: assigneeIds,
                    },
                });
                const oauthUrl = String(response?.url || '').trim();
                if (!oauthUrl) {
                    throw new Error('Google OAuth URL was not returned by server.');
                }
                showInfo('Redirecting to Google consent...');
                window.location.assign(oauthUrl);
                return;
            }

            if (createState.type === 'meta_lead_ads') {
                if (!createState.metaConnected) {
                    throw new Error('Connect your Meta account first.');
                }
                const pageId = String(createState.metaPageId || '').trim();
                const formIds = Array.isArray(createState.metaFormIds)
                    ? createState.metaFormIds.map((id) => String(id || '').trim()).filter(Boolean)
                    : [];
                if (!pageId) throw new Error('Select a Meta Page.');
                if (formIds.length === 0) throw new Error('Select at least one Meta lead form.');

                const payload = {
                    pageId,
                    formIds,
                    sourceFields: Array.isArray(createState.metaSourceFields) ? createState.metaSourceFields : [],
                    mapping: createState.metaMapping || {},
                    transforms: createState.metaTransforms || defaultTransforms,
                };

                if (isMetaConfigure) {
                    const response = await configureMetaLeadSource(editingMetaIntegrationId, payload);
                    setCreateOpen(false);
                    setEditingMetaIntegrationId('');
                    setCreateState(defaultCreateState);
                    if (response?.subscription?.ok === false) {
                        showInfo(`Meta configuration saved, but page subscription failed: ${response?.subscription?.error || 'unknown error'}`);
                    } else {
                        showSuccess('Meta Lead Ads configuration updated');
                    }
                    await loadIntegrations();
                    return;
                }

                const response = await createMetaLeadSource({
                    franchiseId,
                    type: 'meta_lead_ads',
                    routing: {
                        mode: createState.routingMode,
                        assignees: assigneeIds,
                    },
                    ...payload,
                });
                setCreateOpen(false);
                setCreateState(defaultCreateState);
                if (response?.subscription?.ok === false) {
                    showInfo(`Meta source created, but page subscription failed: ${response?.subscription?.error || 'unknown error'}`);
                } else {
                    showSuccess('Meta Lead Ads source connected');
                }
                await loadIntegrations();
                return;
            }

            const response = await createLeadSourceIntegration({
                franchiseId,
                type: 'webhook',
                routing: {
                    mode: createState.routingMode,
                    assignees: assigneeIds,
                },
            });

            setCreatedSecret({
                integrationId: response?.integration?.id,
                webhookUrl: response?.webhook?.url || response?.webhookUrl,
                webhookSecret: response?.webhook?.secret || response?.webhookSecret,
            });
            resetCreateModal();
            showSuccess('Webhook source connected');
            await loadIntegrations();
        } catch (error) {
            const detail = String(error?.message || '').trim();
            showError(detail ? `Failed to create integration: ${detail}` : 'Failed to create integration');
        } finally {
            setCreating(false);
        }
    };

    const handlePauseResume = async (integration) => {
        const integrationId = integration.id;
        updatePending(integrationId, true);
        try {
            if (integration.status === 'paused') {
                await resumeLeadSourceIntegration(integrationId);
                showSuccess('Integration resumed');
            } else {
                await pauseLeadSourceIntegration(integrationId);
                showSuccess('Integration paused');
            }
            await loadIntegrations();
        } catch (error) {
            showError(error.message || 'Failed to update integration');
        } finally {
            updatePending(integrationId, false);
        }
    };

    const handleDeleteIntegration = async (integration) => {
        const integrationId = String(integration?.id || '').trim();
        if (!integrationId) return;
        const label = integrationTypeLabel(integration?.type);
        const confirmed = typeof window === 'undefined'
            ? true
            : window.confirm(`Delete this ${label}? This will stop new leads from this source.`);
        if (!confirmed) return;

        updatePending(integrationId, true);
        try {
            await deleteLeadSourceIntegration(integrationId);
            showSuccess('Lead source deleted');
            if (googleSetupIntegrationId === integrationId) {
                setGoogleSetupIntegrationId('');
            }
            await loadIntegrations();
        } catch (error) {
            showError(error.message || 'Failed to delete lead source');
        } finally {
            updatePending(integrationId, false);
        }
    };

    const handleWebhookTest = async (integration) => {
        const integrationId = integration.id;
        updatePending(integrationId, true);
        try {
            const result = await testLeadSourceIntegration(integrationId);
            if (result?.ok) {
                showSuccess(`Test submitted (${result.eventStatus || 'queued'})`);
            } else {
                showError(result?.error || 'Test failed');
            }
            await loadIntegrations();
        } catch (error) {
            showError(error.message || 'Test failed');
        } finally {
            updatePending(integrationId, false);
        }
    };

    const handleMetaReconnect = async (integration) => {
        if (!integration?.id) return;
        await handleMetaConnect(integration.id);
    };

    const handleMetaBackfill = async (integration, days = 30) => {
        const integrationId = integration?.id;
        if (!integrationId) return;
        updatePending(integrationId, true);
        try {
            const result = await backfillMetaLeadSource(integrationId, { days });
            showSuccess(`Meta backfill queued: ${result?.createdEvents ?? 0} new event(s), ${result?.duplicateEvents ?? 0} duplicate(s)`);
            await loadIntegrations();
        } catch (error) {
            showError(error.message || 'Meta backfill failed');
        } finally {
            updatePending(integrationId, false);
        }
    };

    const handleMetaTest = async (integration) => {
        const integrationId = integration?.id;
        if (!integrationId) return;
        updatePending(integrationId, true);
        try {
            const result = await testMetaLeadSource(integrationId);
            if (result?.ok) {
                const selectedForms = Array.isArray(result?.selectedForms) ? result.selectedForms.length : 0;
                showSuccess(`Meta connection healthy (${selectedForms} selected form${selectedForms === 1 ? '' : 's'})`);
            } else {
                showError('Meta test failed');
            }
            await loadIntegrations();
        } catch (error) {
            showError(error.message || 'Meta test failed');
        } finally {
            updatePending(integrationId, false);
        }
    };

    const handleMetaReplayFailedEvent = async (integrationId, eventId) => {
        if (!integrationId || !eventId) return;
        updatePending(integrationId, true);
        try {
            const result = await replayMetaLeadSourceFailedEvent(integrationId, eventId);
            if (result?.replayed) {
                showSuccess('Failed event queued for replay');
            } else {
                showInfo('Event could not be replayed (already queued/processed)');
            }
            await loadMetaFailedEvents(integrationId);
            await loadIntegrations();
        } catch (error) {
            showError(error.message || 'Failed to replay event');
        } finally {
            updatePending(integrationId, false);
        }
    };

    const handleGoogleRunSync = async (integrationId) => {
        updatePending(integrationId, true);
        try {
            const result = await runGoogleSheetsLeadSourceSync(integrationId);
            if (result?.ok) {
                showSuccess('Google Sheets sync submitted');
            } else {
                showError('Sync did not run');
            }
            await loadIntegrations();
        } catch (error) {
            showError(error.message || 'Failed to run sync');
        } finally {
            updatePending(integrationId, false);
        }
    };

    const handleGoogleSaveConfig = async () => {
        if (!selectedGoogleIntegration || googleConfigSaving) return;
        setGoogleConfigSaving(true);
        try {
            const response = await configureGoogleSheetsLeadSource(selectedGoogleIntegration.id, {
                spreadsheetId: googleConfig.spreadsheetId,
                sheetName: googleConfig.sheetName,
                headerRowIndex: Number(googleConfig.headerRowIndex || 1),
                mode: googleConfig.mode,
                pollIntervalMinutes: Number(googleConfig.pollIntervalMinutes || 5),
                targetMapping: googleMapping,
                transforms: googleTransforms,
            });
            setGooglePreview(response);
            setGoogleMapping(response?.mapping || response?.autoMapping || {});
            setGoogleTransforms(response?.transforms || defaultTransforms);
            showSuccess('Google Sheets mapping saved');
            await loadIntegrations();
        } catch (error) {
            showError(error.message || 'Failed to save Google Sheets config');
        } finally {
            setGoogleConfigSaving(false);
        }
    };

    const handleGoogleSpreadsheetChange = async (spreadsheetId) => {
        setGoogleConfig((prev) => ({
            ...prev,
            spreadsheetId,
            sheetName: '',
        }));
        setGoogleSheetNames([]);
        if (!selectedGoogleIntegration || !spreadsheetId) return;
        await loadGoogleCatalog(selectedGoogleIntegration.id, spreadsheetId);
    };

    const handleGoogleCreateTemplateSheet = async () => {
        if (!selectedGoogleIntegration || googleCreateSheetLoading) return;
        setGoogleCreateSheetLoading(true);
        try {
            const title = `CRM Leads ${new Date().toISOString().slice(0, 10)}`;
            const response = await createGoogleSheetsLeadSourceSpreadsheet(selectedGoogleIntegration.id, {
                title,
                sheetName: 'Leads',
                headerRowIndex: Number(googleConfig.headerRowIndex || 1),
            });
            const spreadsheetId = String(response?.spreadsheet?.id || '').trim();
            const sheetName = String(response?.sheetName || 'Leads').trim() || 'Leads';
            if (!spreadsheetId) {
                throw new Error('Spreadsheet was created but no spreadsheetId was returned.');
            }
            setGoogleConfig((prev) => ({
                ...prev,
                spreadsheetId,
                sheetName,
            }));
            showSuccess('New Google Sheet template created with lead fields.');
            await loadGoogleCatalog(selectedGoogleIntegration.id, spreadsheetId);
        } catch (error) {
            showError(error.message || 'Failed to create template sheet');
        } finally {
            setGoogleCreateSheetLoading(false);
        }
    };

    const handleGoogleSetupPush = async () => {
        if (!selectedGoogleIntegration || googlePushLoading) return;
        setGooglePushLoading(true);
        try {
            const data = await setupGoogleSheetsLeadSourcePush(selectedGoogleIntegration.id);
            setGooglePushData(data);
            showSuccess('Push mode setup complete.');
            await loadIntegrations();
        } catch (error) {
            showError(error.message || 'Failed to setup push mode');
        } finally {
            setGooglePushLoading(false);
        }
    };

    const handleGoogleTestPush = async () => {
        if (!selectedGoogleIntegration || googlePushTesting) return;
        setGooglePushTesting(true);
        try {
            const result = await testGoogleSheetsLeadSourcePush(selectedGoogleIntegration.id);
            if (result?.ok) {
                showSuccess('Push test sent successfully');
            } else {
                showError('Push test failed');
            }
            await loadIntegrations();
        } catch (error) {
            showError(error.message || 'Push test failed');
        } finally {
            setGooglePushTesting(false);
        }
    };

    if (isCrmPortalMode()) {
        return (
            <div className="crm-empty-state">
                <h3>Lead Sources is owner-only</h3>
                <p>This section is managed from the owner dashboard.</p>
            </div>
        );
    }

    return (
        <div className="crm-page crm-lead-sources-page">
            <div className="crm-toolbar">
                <div className="crm-toolbar-row">
                    <div>
                        <h1 className="crm-page-title">Lead Sources</h1>
                        <p className="crm-text-muted">
                            Connect ad platforms, forms, and automation tools. Webhook, Google Sheets, and Meta Lead Ads connectors are available.
                        </p>
                    </div>
                    <div className="crm-toolbar-actions">
                        <button className="crm-btn crm-btn-primary" type="button" onClick={() => setCreateOpen(true)}>
                            Connect Source
                        </button>
                        <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" onClick={loadIntegrations}>
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {createdSecret && (
                <div className="crm-card" style={{ marginBottom: 16 }}>
                    <div className="crm-card-body">
                        <h3 style={{ marginTop: 0 }}>Webhook Generated</h3>
                        <p className="crm-text-muted">Save this secret now. It will not be shown again.</p>
                        <div className="crm-inline-field" style={{ marginBottom: 8 }}>
                            <input className="crm-input" readOnly value={createdSecret.webhookUrl || ''} />
                            <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" onClick={() => handleCopy('Webhook URL', createdSecret.webhookUrl)}>
                                Copy URL
                            </button>
                        </div>
                        <div className="crm-inline-field">
                            <input className="crm-input" readOnly value={createdSecret.webhookSecret || ''} />
                            <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" onClick={() => handleCopy('Webhook secret', createdSecret.webhookSecret)}>
                                Copy Secret
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {selectedGoogleIntegration && (
                <div className="crm-card" style={{ marginBottom: 16 }}>
                    <div className="crm-card-body">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                            <h3 style={{ margin: 0 }}>Google Sheets Setup Wizard</h3>
                            <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" onClick={() => setGoogleSetupIntegrationId('')}>
                                Close
                            </button>
                        </div>
                        <p className="crm-text-muted" style={{ marginTop: 8 }}>
                            Configure spreadsheet mapping, mode, and optional push bridge for row edit updates.
                        </p>

                        <div className="crm-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
                            <div className="crm-form-group">
                                <label className="crm-label">Spreadsheet</label>
                                <select
                                    className="crm-select"
                                    value={googleConfig.spreadsheetId}
                                    onChange={(event) => handleGoogleSpreadsheetChange(event.target.value)}
                                    disabled={googleCatalogLoading}
                                >
                                    <option value="">Select spreadsheet...</option>
                                    {googleConfig.spreadsheetId && !googleSpreadsheets.some((entry) => entry.id === googleConfig.spreadsheetId) && (
                                        <option value={googleConfig.spreadsheetId}>
                                            {googleConfig.spreadsheetId}
                                        </option>
                                    )}
                                    {googleSpreadsheets.map((sheet) => (
                                        <option key={sheet.id} value={sheet.id}>
                                            {sheet.title}
                                        </option>
                                    ))}
                                </select>
                                <div className="crm-toolbar-actions" style={{ marginTop: 8, gap: 8 }}>
                                    <button
                                        className="crm-btn crm-btn-secondary crm-btn-sm"
                                        type="button"
                                        onClick={() => loadGoogleCatalog(selectedGoogleIntegration.id, googleConfig.spreadsheetId)}
                                        disabled={googleCatalogLoading}
                                    >
                                        {googleCatalogLoading ? 'Loading...' : 'Refresh Sheets'}
                                    </button>
                                    <button
                                        className="crm-btn crm-btn-secondary crm-btn-sm"
                                        type="button"
                                        onClick={handleGoogleCreateTemplateSheet}
                                        disabled={googleCreateSheetLoading}
                                    >
                                        {googleCreateSheetLoading ? 'Creating...' : 'Create Lead Template'}
                                    </button>
                                </div>
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">Sheet Name</label>
                                <select
                                    className="crm-select"
                                    value={googleConfig.sheetName}
                                    onChange={(event) => setGoogleConfig((prev) => ({ ...prev, sheetName: event.target.value }))}
                                    disabled={!googleConfig.spreadsheetId || googleCatalogLoading}
                                >
                                    <option value="">{googleConfig.spreadsheetId ? 'Select tab...' : 'Select spreadsheet first'}</option>
                                    {googleConfig.sheetName && !googleSheetNames.includes(googleConfig.sheetName) && (
                                        <option value={googleConfig.sheetName}>{googleConfig.sheetName}</option>
                                    )}
                                    {googleSheetNames.map((name) => (
                                        <option key={name} value={name}>{name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">Header Row</label>
                                <input
                                    className="crm-input"
                                    type="number"
                                    min="1"
                                    value={googleConfig.headerRowIndex}
                                    onChange={(event) => setGoogleConfig((prev) => ({ ...prev, headerRowIndex: Number(event.target.value || 1) }))}
                                />
                            </div>
                        </div>
                        {selectedSpreadsheetMeta?.webViewLink && (
                            <p className="crm-text-muted" style={{ marginTop: 8 }}>
                                Selected file:{' '}
                                <a href={selectedSpreadsheetMeta.webViewLink} target="_blank" rel="noreferrer">
                                    {selectedSpreadsheetMeta.title}
                                </a>
                            </p>
                        )}

                        <div className="crm-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
                            <div className="crm-form-group">
                                <label className="crm-label">Mode</label>
                                <select
                                    className="crm-select"
                                    value={googleConfig.mode}
                                    onChange={(event) => setGoogleConfig((prev) => ({ ...prev, mode: event.target.value === 'push' ? 'push' : 'polling' }))}
                                >
                                    <option value="polling">Polling (new rows)</option>
                                    <option value="push">Real-time push (edits + new rows)</option>
                                </select>
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">Poll Interval Minutes</label>
                                <input
                                    className="crm-input"
                                    type="number"
                                    min="1"
                                    value={googleConfig.pollIntervalMinutes}
                                    onChange={(event) => setGoogleConfig((prev) => ({ ...prev, pollIntervalMinutes: Number(event.target.value || 5) }))}
                                />
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">Integration ID</label>
                                <input className="crm-input" readOnly value={selectedGoogleIntegration.id} />
                            </div>
                        </div>

                        <div className="crm-toolbar-actions" style={{ marginTop: 8 }}>
                            <button className="crm-btn crm-btn-primary" type="button" onClick={handleGoogleSaveConfig} disabled={googleConfigSaving}>
                                {googleConfigSaving ? 'Saving...' : 'Preview & Save Mapping'}
                            </button>
                            <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" onClick={() => handleGoogleRunSync(selectedGoogleIntegration.id)}>
                                Run Sync Now
                            </button>
                            {googleConfig.mode === 'push' && (
                                <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" onClick={handleGoogleSetupPush} disabled={googlePushLoading}>
                                    {googlePushLoading ? 'Setting up...' : 'Setup Push'}
                                </button>
                            )}
                            {googleConfig.mode === 'push' && (
                                <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" onClick={handleGoogleTestPush} disabled={googlePushTesting}>
                                    {googlePushTesting ? 'Testing...' : 'Test Push'}
                                </button>
                            )}
                        </div>

                        {googlePreview?.sourceFields?.length > 0 && (
                            <div className="crm-card" style={{ marginTop: 12 }}>
                                <div className="crm-card-body">
                                    <h4 style={{ margin: 0, marginBottom: 8 }}>Field Mapping</h4>
                                    <div className="crm-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                                        {TARGET_FIELDS.map((field) => (
                                            <div className="crm-form-group" key={field.key}>
                                                <label className="crm-label">{field.label}</label>
                                                <select
                                                    className="crm-select"
                                                    value={googleMapping[field.key] || ''}
                                                    onChange={(event) => {
                                                        const value = event.target.value;
                                                        setGoogleMapping((prev) => ({ ...prev, [field.key]: value || undefined }));
                                                    }}
                                                >
                                                    <option value="">-- Not mapped --</option>
                                                    {googlePreview.sourceFields.map((sourceField) => (
                                                        <option key={`${field.key}_${sourceField.key}`} value={sourceField.key}>
                                                            {sourceField.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="crm-text-muted">Mapping must include at least phone or email.</p>
                                </div>
                            </div>
                        )}

                        {googlePushData && (
                            <div className="crm-card" style={{ marginTop: 12 }}>
                                <div className="crm-card-body">
                                    <h4 style={{ margin: 0, marginBottom: 8 }}>Apps Script Push Setup</h4>
                                    <div className="crm-inline-field" style={{ marginBottom: 8 }}>
                                        <input className="crm-input" readOnly value={googlePushData.pushUrl || ''} />
                                        <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" onClick={() => handleCopy('Push URL', googlePushData.pushUrl)}>
                                            Copy URL
                                        </button>
                                    </div>
                                    <div className="crm-inline-field" style={{ marginBottom: 8 }}>
                                        <input className="crm-input" readOnly value={googlePushData.pushSecret || ''} />
                                        <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" onClick={() => handleCopy('Push Secret', googlePushData.pushSecret)}>
                                            Copy Secret
                                        </button>
                                    </div>
                                    <div className="crm-form-group">
                                        <label className="crm-label">Apps Script</label>
                                        <textarea className="crm-textarea" rows={12} readOnly value={googlePushData.appsScript || ''} />
                                    </div>
                                    <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" onClick={() => handleCopy('Apps Script', googlePushData.appsScript)}>
                                        Copy Script
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {loading ? (
                <div className="crm-loading"><div className="crm-spinner" /></div>
            ) : items.length === 0 ? (
                <div className="crm-empty-state">
                    <h3>No lead sources connected</h3>
                    <p>Connect your first source to ingest leads automatically.</p>
                    <button className="crm-btn crm-btn-primary" type="button" onClick={() => setCreateOpen(true)}>
                        Connect Source
                    </button>
                </div>
            ) : (
                <div className="crm-grid" style={{ gap: 12 }}>
                    {items.map((integration) => {
                        const pending = !!pendingById[integration.id];
                        const isWebhook = integration.type === 'webhook';
                        const isGoogleSheets = integration.type === 'google_sheets';
                        const isMeta = integration.type === 'meta_lead_ads';
                        const metaFailedEvents = Array.isArray(metaFailedEventsByIntegration[integration.id])
                            ? metaFailedEventsByIntegration[integration.id]
                            : [];
                        const badgeClass = integration.status === 'connected'
                            ? 'crm-badge-success'
                            : (integration.status === 'paused' ? 'crm-badge-warning' : (integration.status === 'needs_reauth' ? 'crm-badge-warning' : 'crm-badge-danger'));
                        return (
                            <div className="crm-card" key={integration.id}>
                                <div className="crm-card-body">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                                        <div>
                                            <h3 style={{ margin: 0 }}>{integrationTypeLabel(integration.type)}</h3>
                                            <div className="crm-text-muted">{integrationTypeHint(integration)}</div>
                                        </div>
                                        <span className={`crm-badge ${badgeClass}`}>
                                            {integration.status}
                                        </span>
                                    </div>

                                    <div className="crm-grid" style={{ marginTop: 12, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                                        <div>
                                            <div className="crm-text-muted">Last event</div>
                                            <strong>{formatDateTime(integration.lastEventAt)}</strong>
                                        </div>
                                        <div>
                                            <div className="crm-text-muted">{isGoogleSheets ? 'Sheet hits today' : isMeta ? 'Leads today' : 'Webhook hits today'}</div>
                                            <strong>{integration.leadsReceivedToday ?? 0}</strong>
                                        </div>
                                        <div>
                                            <div className="crm-text-muted">{isGoogleSheets ? 'Last sync' : isMeta ? 'Last webhook' : 'Last seen'}</div>
                                            <strong>{isGoogleSheets ? formatDateTime(integration?.googleSheets?.lastSyncAt) : isMeta ? formatDateTime(integration?.meta?.lastWebhookAt) : formatDateTime(integration?.webhook?.lastSeenAt)}</strong>
                                        </div>
                                    </div>

                                    {isMeta && (
                                        <div className="crm-card" style={{ marginTop: 12 }}>
                                            <div className="crm-card-body" style={{ padding: 12 }}>
                                                <div className="crm-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                                                    <div>
                                                        <div className="crm-text-muted">Page</div>
                                                        <strong>{integration?.meta?.pageName || integration?.meta?.pageId || '-'}</strong>
                                                    </div>
                                                    <div>
                                                        <div className="crm-text-muted">Subscribed</div>
                                                        <strong>{integration?.meta?.subscribed ? 'Yes' : 'No'}</strong>
                                                    </div>
                                                    <div>
                                                        <div className="crm-text-muted">Forms</div>
                                                        <div>{Array.isArray(integration?.meta?.formIds) && integration.meta.formIds.length > 0 ? integration.meta.formIds.length : 0}</div>
                                                    </div>
                                                    <div>
                                                        <div className="crm-text-muted">Last sync</div>
                                                        <div>{formatDateTime(integration?.meta?.lastSyncAt)}</div>
                                                    </div>
                                                </div>
                                                {integration?.meta?.lastError && (
                                                    <div className="crm-text-muted" style={{ marginTop: 8, color: '#c96b6b' }}>
                                                        Last error: {String(integration.meta.lastError)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {isWebhook && (
                                        <div className="crm-inline-field" style={{ marginTop: 12 }}>
                                            <input className="crm-input" readOnly value={integration.webhookUrl || ''} />
                                            <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" onClick={() => handleCopy('Webhook URL', integration.webhookUrl)}>
                                                Copy URL
                                            </button>
                                        </div>
                                    )}

                                    <div className="crm-toolbar-actions" style={{ marginTop: 12 }}>
                                        {isWebhook && (
                                            <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" disabled={pending} onClick={() => handleWebhookTest(integration)}>
                                                Send Test
                                            </button>
                                        )}
                                        {isGoogleSheets && (
                                            <>
                                                <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" disabled={pending} onClick={() => handleGoogleRunSync(integration.id)}>
                                                    Run Sync
                                                </button>
                                                <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" onClick={() => setGoogleSetupIntegrationId(integration.id)}>
                                                    Configure
                                                </button>
                                            </>
                                        )}
                                        {isMeta && (
                                            <>
                                                <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" disabled={pending} onClick={() => openMetaConfigureFlow(integration)}>
                                                    Configure
                                                </button>
                                                <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" disabled={pending} onClick={() => handleMetaBackfill(integration, 30)}>
                                                    Backfill 30 Days
                                                </button>
                                                <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" disabled={pending} onClick={() => handleMetaTest(integration)}>
                                                    Test
                                                </button>
                                                <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" disabled={pending} onClick={() => handleMetaReconnect(integration)}>
                                                    Reconnect
                                                </button>
                                                <button
                                                    className="crm-btn crm-btn-secondary crm-btn-sm"
                                                    type="button"
                                                    disabled={pending || metaFailedEventsLoading}
                                                    onClick={() => {
                                                        if (metaFailedEventsOpenFor === integration.id) {
                                                            setMetaFailedEventsOpenFor('');
                                                        } else {
                                                            loadMetaFailedEvents(integration.id);
                                                        }
                                                    }}
                                                >
                                                    {metaFailedEventsOpenFor === integration.id ? 'Hide Failed Events' : 'Failed Events'}
                                                </button>
                                            </>
                                        )}
                                        <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" disabled={pending} onClick={() => handlePauseResume(integration)}>
                                            {integration.status === 'paused' ? 'Resume' : 'Pause'}
                                        </button>
                                        <button
                                            className="crm-btn crm-btn-secondary crm-btn-sm"
                                            type="button"
                                            disabled={pending}
                                            onClick={() => handleDeleteIntegration(integration)}
                                        >
                                            Delete
                                        </button>
                                    </div>

                                    {isMeta && metaFailedEventsOpenFor === integration.id && (
                                        <div className="crm-card" style={{ marginTop: 12 }}>
                                            <div className="crm-card-body" style={{ padding: 12 }}>
                                                <h4 style={{ margin: 0, marginBottom: 8 }}>Failed Events</h4>
                                                {metaFailedEvents.length === 0 ? (
                                                    <p className="crm-text-muted" style={{ margin: 0 }}>No failed events found.</p>
                                                ) : (
                                                    <div style={{ display: 'grid', gap: 8 }}>
                                                        {metaFailedEvents.map((event) => (
                                                            <div key={event.id} className="crm-card" style={{ margin: 0 }}>
                                                                <div className="crm-card-body" style={{ padding: 10 }}>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                                                                        <div>
                                                                            <div><strong>{event.sourceEventId || event.id}</strong></div>
                                                                            <div className="crm-text-muted">{formatDateTime(event.processedAt || event.receivedAt)}</div>
                                                                            {event.error && <div className="crm-text-muted" style={{ color: '#c96b6b' }}>{String(event.error)}</div>}
                                                                        </div>
                                                                        <button
                                                                            className="crm-btn crm-btn-secondary crm-btn-sm"
                                                                            type="button"
                                                                            disabled={pending}
                                                                            onClick={() => handleMetaReplayFailedEvent(integration.id, event.id)}
                                                                        >
                                                                            Replay
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {createOpen && (
                <div className="crm-modal-overlay" onClick={resetCreateModal}>
                    <div className="crm-modal crm-lead-sources-modal" onClick={(event) => event.stopPropagation()} style={{ maxWidth: 700, width: '100%' }}>
                        <div className="crm-modal-header crm-lead-sources-modal-header">
                            <h2 className="crm-modal-title">{isMetaConfigureMode ? 'Configure Meta Lead Ads' : 'Connect Source'}</h2>
                            <button className="crm-modal-close" onClick={resetCreateModal}>X</button>
                        </div>
                        <div className="crm-modal-body crm-lead-sources-modal-body">
                            <div className="crm-grid" style={{ gridTemplateColumns: !isMetaConfigureMode ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: 12 }}>
                                <div className="crm-form-group">
                                    <label className="crm-label">Source Type</label>
                                    <select
                                        className="crm-select"
                                        value={createState.type}
                                        disabled={!!editingMetaIntegrationId}
                                        onChange={(event) => {
                                            const nextType = event.target.value;
                                            setEditingMetaIntegrationId('');
                                            setCreateState((prev) => ({
                                                ...defaultCreateState,
                                                type: nextType,
                                                routingMode: prev.routingMode,
                                                assigneeIds: prev.assigneeIds,
                                            }));
                                        }}
                                    >
                                        {SOURCE_TYPES.map((entry) => (
                                            <option key={entry.value} value={entry.value}>{entry.label}</option>
                                        ))}
                                    </select>
                                </div>
                                {!isMetaConfigureMode && (
                                    <div className="crm-form-group">
                                        <label className="crm-label">Routing Mode</label>
                                        <select className="crm-select" value={createState.routingMode} onChange={(event) => handleRoutingModeChange(event.target.value)}>
                                            <option value="fixed_owner">Assign to Fixed Owner</option>
                                            <option value="round_robin">Round Robin</option>
                                        </select>
                                    </div>
                                )}
                            </div>

                            {!isMetaConfigureMode && (
                                <div className="crm-form-group">
                                    <label className="crm-label">Assignees</label>
                                    <div className="crm-card crm-lead-sources-assignees" style={{ maxHeight: 220, overflow: 'auto' }}>
                                        <div className="crm-card-body">
                                            {activeSeats.length === 0 ? (
                                                <p className="crm-text-muted" style={{ margin: 0 }}>No active seats found.</p>
                                            ) : activeSeats.map((seat) => (
                                                <label key={seat.userId} className="crm-inline-check" style={{ display: 'block', marginBottom: 8 }}>
                                                    <input type="checkbox" checked={createState.assigneeIds.includes(seat.userId)} onChange={() => handleAssigneeToggle(seat.userId)} />
                                                    <span>{seat.name} ({seat.role})</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {createState.type === 'google_sheets' && (
                                <div className="crm-card" style={{ marginTop: 8 }}>
                                    <div className="crm-card-body">
                                        <p className="crm-text-muted" style={{ margin: 0 }}>
                                            After clicking connect, authorize Google and you will return here to complete mapping.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {isMetaWizard && (
                                <div className="crm-card" style={{ marginTop: 8 }}>
                                    <div className="crm-card-body">
                                        <h3 style={{ marginTop: 0, marginBottom: 8 }}>
                                            {isMetaConfigureMode ? 'Meta Lead Ads Configuration' : 'Meta Lead Ads Setup Wizard'}
                                        </h3>
                                        <p className="crm-text-muted" style={{ marginTop: 0 }}>
                                            {isMetaConfigureMode
                                                ? 'Update Page/forms, refresh mapping, test connectivity, or reconnect if the token expired.'
                                                : 'Connect Meta, choose a Page and form(s), optionally map fields, then activate.'}
                                        </p>

                                        <div className="crm-card" style={{ marginBottom: 12 }}>
                                            <div className="crm-card-body" style={{ padding: 12 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                    <div>
                                                        <div className="crm-text-muted">Step 1</div>
                                                        <strong>{createState.metaConnected ? 'Meta account connected' : 'Connect Meta account'}</strong>
                                                    </div>
                                                    <div className="crm-toolbar-actions" style={{ gap: 8 }}>
                                                        <button
                                                            className="crm-btn crm-btn-secondary crm-btn-sm"
                                                            type="button"
                                                            onClick={async () => {
                                                                const pages = await loadMetaPages();
                                                                const selectedPageId = String(createState.metaPageId || pages?.[0]?.pageId || '').trim();
                                                                if (selectedPageId) await loadMetaForms(selectedPageId);
                                                            }}
                                                            disabled={metaPagesLoading}
                                                        >
                                                            {metaPagesLoading ? 'Loading...' : (createState.metaConnected ? 'Refresh Pages' : 'Use Existing Meta Connection')}
                                                        </button>
                                                        <button
                                                            className="crm-btn crm-btn-secondary crm-btn-sm"
                                                            type="button"
                                                            onClick={() => handleMetaConnect(editingMetaIntegrationId)}
                                                        >
                                                            {createState.metaConnected ? 'Reconnect Meta' : 'Connect Meta'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {createState.metaConnected && (
                                            <>
                                                <div className="crm-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                                                    <div className="crm-form-group">
                                                        <label className="crm-label">Step 2: Meta Page</label>
                                                        <select
                                                            className="crm-select"
                                                            value={createState.metaPageId || ''}
                                                            onChange={async (event) => {
                                                                const nextPageId = event.target.value;
                                                                setCreateState((prev) => ({
                                                                    ...prev,
                                                                    metaPageId: nextPageId,
                                                                    metaForms: [],
                                                                    metaFormIds: [],
                                                                    metaSourceFields: [],
                                                                    metaSampleLead: null,
                                                                }));
                                                                await loadMetaForms(nextPageId);
                                                            }}
                                                            disabled={metaPagesLoading}
                                                        >
                                                            <option value="">Select page...</option>
                                                            {(createState.metaPages || []).map((page) => (
                                                                <option key={page.pageId} value={page.pageId}>
                                                                    {page.pageName} {page.hasLeadAccess === false ? '(No lead access)' : ''}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="crm-form-group">
                                                        <label className="crm-label">Step 3: Lead Form(s)</label>
                                                        <div className="crm-card" style={{ maxHeight: 160, overflow: 'auto' }}>
                                                            <div className="crm-card-body" style={{ padding: 12 }}>
                                                                {metaFormsLoading ? (
                                                                    <p className="crm-text-muted" style={{ margin: 0 }}>Loading forms...</p>
                                                                ) : (createState.metaForms || []).length === 0 ? (
                                                                    <p className="crm-text-muted" style={{ margin: 0 }}>Select a Page to load forms.</p>
                                                                ) : (
                                                                    (createState.metaForms || []).map((form) => (
                                                                        <label key={form.formId} className="crm-inline-check" style={{ display: 'block', marginBottom: 8 }}>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={(createState.metaFormIds || []).includes(form.formId)}
                                                                                onChange={() => {
                                                                                    setCreateState((prev) => {
                                                                                        const current = Array.isArray(prev.metaFormIds) ? prev.metaFormIds : [];
                                                                                        const exists = current.includes(form.formId);
                                                                                        return {
                                                                                            ...prev,
                                                                                            metaFormIds: exists
                                                                                                ? current.filter((id) => id !== form.formId)
                                                                                                : [...current, form.formId],
                                                                                        };
                                                                                    });
                                                                                }}
                                                                            />
                                                                            <span>{form.formName || form.formId}</span>
                                                                        </label>
                                                                    ))
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="crm-card" style={{ marginTop: 12 }}>
                                                    <div className="crm-card-body" style={{ padding: 12 }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                            <div>
                                                                <div className="crm-text-muted">Step 4</div>
                                                                <strong>Field Mapping (Optional)</strong>
                                                            </div>
                                                            <button
                                                                className="crm-btn crm-btn-secondary crm-btn-sm"
                                                                type="button"
                                                                disabled={metaSampleLoading || !createState.metaPageId || !selectedMetaFormForSample}
                                                                onClick={() => loadMetaSample(selectedMetaFormForSample, createState.metaPageId)}
                                                            >
                                                                {metaSampleLoading ? 'Loading Sample...' : 'Load Sample Fields'}
                                                            </button>
                                                        </div>
                                                        <p className="crm-text-muted" style={{ marginTop: 8, marginBottom: 8 }}>
                                                            Mapping is optional. If omitted, the system will use best-effort auto mapping and still require phone or email.
                                                        </p>

                                                        {createState.metaSampleLead && (
                                                            <div className="crm-text-muted" style={{ marginBottom: 8 }}>
                                                                Sample lead loaded: {createState.metaSampleLead.id || '-'} ({formatDateTime(createState.metaSampleLead.createdTime)})
                                                            </div>
                                                        )}

                                                        <div className="crm-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
                                                            <label className="crm-inline-check">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={createState.metaTransforms?.phoneNormalize !== false}
                                                                    onChange={(event) => setCreateState((prev) => ({
                                                                        ...prev,
                                                                        metaTransforms: { ...(prev.metaTransforms || {}), phoneNormalize: !!event.target.checked },
                                                                    }))}
                                                                />
                                                                <span>Normalize phone</span>
                                                            </label>
                                                            <label className="crm-inline-check">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={createState.metaTransforms?.emailLower !== false}
                                                                    onChange={(event) => setCreateState((prev) => ({
                                                                        ...prev,
                                                                        metaTransforms: { ...(prev.metaTransforms || {}), emailLower: !!event.target.checked },
                                                                    }))}
                                                                />
                                                                <span>Lowercase email</span>
                                                            </label>
                                                            <label className="crm-inline-check">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={createState.metaTransforms?.utmExtract !== false}
                                                                    onChange={(event) => setCreateState((prev) => ({
                                                                        ...prev,
                                                                        metaTransforms: { ...(prev.metaTransforms || {}), utmExtract: !!event.target.checked },
                                                                    }))}
                                                                />
                                                                <span>Extract UTM</span>
                                                            </label>
                                                        </div>

                                                        {(createState.metaSourceFields || []).length > 0 && (
                                                            <div className="crm-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, marginTop: 10 }}>
                                                                {TARGET_FIELDS.map((field) => (
                                                                    <div className="crm-form-group" key={`meta_${field.key}`}>
                                                                        <label className="crm-label">{field.label}</label>
                                                                        <select
                                                                            className="crm-select"
                                                                            value={createState.metaMapping?.[field.key] || ''}
                                                                            onChange={(event) => {
                                                                                const value = event.target.value;
                                                                                setCreateState((prev) => ({
                                                                                    ...prev,
                                                                                    metaMapping: {
                                                                                        ...(prev.metaMapping || {}),
                                                                                        [field.key]: value || undefined,
                                                                                    },
                                                                                }));
                                                                            }}
                                                                        >
                                                                            <option value="">-- Auto / Not mapped --</option>
                                                                            {(createState.metaSourceFields || []).map((sourceField) => (
                                                                                <option key={`meta_${field.key}_${sourceField.key}`} value={sourceField.key}>
                                                                                    {sourceField.label}{sourceField.sampleValue ? ` (${sourceField.sampleValue})` : ''}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {!isMetaConfigureMode && (
                                                    <div className="crm-card" style={{ marginTop: 12 }}>
                                                        <div className="crm-card-body" style={{ padding: 12 }}>
                                                            <div className="crm-text-muted">Step 5</div>
                                                            <strong>Routing is configured above</strong>
                                                            <p className="crm-text-muted" style={{ marginBottom: 0 }}>
                                                                Fixed owner needs exactly one assignee. Round robin needs at least two assignees.
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="crm-toolbar-actions crm-lead-sources-modal-actions" style={{ marginTop: 16 }}>
                                <button className="crm-btn crm-btn-secondary" type="button" onClick={resetCreateModal}>
                                    Cancel
                                </button>
                                <button
                                    className="crm-btn crm-btn-primary"
                                    type="button"
                                    onClick={handleCreate}
                                    disabled={creating || (isMetaWizard && !createState.metaConnected)}
                                >
                                    {creating
                                        ? 'Working...'
                                        : (isMetaConfigureMode
                                            ? 'Save Meta Configuration'
                                            : (createState.type === 'google_sheets'
                                                ? 'Connect Google Sheets'
                                                : (createState.type === 'meta_lead_ads' ? 'Activate Meta Lead Ads' : 'Create Webhook')))}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LeadSourcesPage;
